import React, { useEffect, useRef, useState } from 'react';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTranslation } from 'react-i18next';
import { formatStopNumber } from 'utils/stopNumberUtils';
import { useMapBoxPageLoadTrack } from 'graphql/mutations/mapBoxTrackLog';

// 设置Mapbox访问令牌
const accessToken = 'pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ';
(mapboxgl as any).accessToken = accessToken;

// 创建当前位置标记的蓝色圆圈
const createLocationMarker = () => {
    const el = document.createElement('div');
    el.style.width = '16px';
    el.style.height = '16px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = '#3B82F6';
    el.style.border = '2px solid white';
    el.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 0 8px rgba(59, 130, 246, 0.2)';
    el.style.position = 'absolute';
    el.style.zIndex = '1000';
    el.style.transform = 'translate(-50%, -50%)'; // 确保圆圈中心对齐到坐标点
    el.style.pointerEvents = 'none'; // 禁用指针事件，确保不会阻挡包裹标记的点击
    el.classList.add('current-location-marker');// 添加类名以便识别

    return el;
};

// 创建带数字的标记元素 - 使用大头针样式，根据传入的颜色设置
const createNumberedMarker = (number: number | string, markerColor: string) => {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.cursor = 'pointer';
    el.style.zIndex = '1000';
    el.style.width = '32px';
    el.style.height = '40px';
    el.style.transform = 'translate(-50%, -100%)'; // 确保大头针底部对齐到坐标点
    el.style.pointerEvents = 'auto'; // 明确启用指针事件


    // 创建大头针 SVG
    el.innerHTML = `
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C8 0 0 7 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7 24 0 16 0Z" fill="${markerColor}"/>
    </svg>
    <span style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: #fff;
      font-weight: bold;
      line-height: 1;
      user-select: none;
      pointer-events: none;
      letter-spacing: 1px;
      margin-top: -0.5em;
    ">
      ${number}
    </span>
  `;

    // 添加地址标记的类名以便后续清除
    el.classList.add('address-marker');

    return el;
};

// 创建集群标记元素 - 锥形底部+单数样式，与普通大头针区分，仅显示单数
const createClusterMarker = (stopNumber: string, count: number, markerColor: string = '#3B82F6') => {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.cursor = 'pointer';
    el.style.zIndex = '1000';
    el.style.width = '40px';
    el.style.height = '50px';
    // 确保底部精确对齐：使用 translate(-50%, -100%) 让底部中心点对齐到坐标点
    // 锥形底部在 viewBox 的 y=47.5 位置，对应实际高度的 50px，所以底部正好在坐标点
    el.style.transform = 'translate(-50%, -100%)';
    el.style.transformOrigin = '50% 100%'; // 变换原点在底部中心
    el.style.pointerEvents = 'auto';
    el.style.boxSizing = 'border-box';

    // 根据数量动态调整字体大小
    const countText = count.toString();
    const fontSize = countText.length > 2 ? '14px' : countText.length > 1 ? '15px' : '16px';

    // 生成唯一ID，避免多个标记冲突
    const uniqueId = `cluster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 创建锥形底部+单数样式，与普通大头针区分
    // 锥形路径：上半部分是圆形，下半部分是锥形（尖底）
    // 使用纯黑色，不使用渐变
    el.innerHTML = `
        <!-- 锥形大头针样式（比单个标记更大，纯黑色） -->
        <svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0; display: block;">
            <defs>
                <!-- 阴影滤镜 -->
                <filter id="clusterShadow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                    <feOffset dx="0" dy="2" result="offsetblur"/>
                    <feComponentTransfer>
                        <feFuncA type="linear" slope="0.4"/>
                    </feComponentTransfer>
                    <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            <path 
                d="M 20 2.5 C 11.75 2.5 5 9.25 5 17.5 C 5 25.75 20 47.5 20 47.5 C 20 47.5 35 25.75 35 17.5 C 35 9.25 28.25 2.5 20 2.5 Z" 
                fill="${markerColor}"
                filter="url(#clusterShadow-${uniqueId})"
            />
            <!-- 白色边框：增强对比，与普通标记区分 -->
            <path 
                d="M 20 2.5 C 11.75 2.5 5 9.25 5 17.5 C 5 25.75 20 47.5 20 47.5 C 20 47.5 35 25.75 35 17.5 C 35 9.25 28.25 2.5 20 2.5 Z" 
                fill="none"
                stroke="white"
                stroke-width="2"
            />
            <!-- 顶部立体菱形 + 两个长V形堆叠（白色轮廓，间距一致） -->
            <defs>
                <!-- 菱形渐变（立体感） -->
                <linearGradient id="diamondGradient-${uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#e0e0e0;stop-opacity:1" />
                </linearGradient>
            </defs>
            <g transform="translate(20, 20)">
                <!-- 第三个V形（最底层，更长） -->
                <g transform="translate(0, 5)">
                    <path d="M -10 -2 L 0 5 L 10 -2" fill="none" stroke="white" stroke-width="1.5" opacity="1"/>
                </g>
                <!-- 第二个V形（中间层，更长） -->
                <g transform="translate(0, 0)">
                    <path d="M -10 -2 L 0 5 L 10 -2" fill="none" stroke="white" stroke-width="2" opacity="1"/>
                </g>
                <!-- 第一个菱形（最上层，立体感） -->
                <g transform="translate(0, -5)">
                    <path d="M 0 -4 L 6 0 L 0 4 L -6 0 Z" fill="none" stroke="white" stroke-width="2.5" opacity="1"/>
                    <!-- 高光效果（立体感） -->
                    <path d="M 0 -4 L 3 -1 L 0 1 L -3 -1 Z" fill="none" stroke="white" stroke-width="1.5" opacity="0.6"/>
                </g>
            </g>
        </svg>
        <!-- 订单数量（显示在大头针上方，长方形样式，带指向箭头） -->
        <div style="
            position: absolute;
            top: -32px;
            left: 50%;
            transform: translate(-50%, 0);
            display: flex;
            flex-direction: column;
            align-items: center;
            user-select: none;
            pointer-events: auto;
            cursor: pointer;
        ">
            <!-- 长方形标签 -->
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                color: #fff;
                font-weight: bold;
                line-height: 1;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
                background-color: ${markerColor};
                border-radius: 4px;
                padding: 4px 12px;
                white-space: nowrap;
                width: 40px;
                box-sizing: border-box;
            ">
                ${count}
            </div>
            <!-- 指向箭头（指向下方的锥形样式） -->
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-top: -1px;">
                <path d="M 6 8 L 0 0 L 12 0 Z" fill="${markerColor}"/>
            </svg>
        </div>
    `;

    el.classList.add('cluster-marker');
    return el;
};

interface MapboxMapProps {
    deliveryAddresses: Array<{
        id: string;
        address: string;
        trackingNumber: string;
        deliveryTaskOrderId: number;
        stopNumber: number | string;
        subStopNumber: string;
        isAbnormal?: boolean; // 异常状态字段
        localMarkerColor?: string; // 标记颜色
        latitude?: number; // 纬度
        longitude?: number; // 经度
        routeName?: string;
        orderWithAddress?: any;
    }>;
    onMarkerClick?: (markerData: any) => void;
    onMapClick?: () => void;
    mapRef?: React.MutableRefObject<any>; // 外部地图引用
    getSavedMapState?: () => any; // 获取保存的地图状态
    showCurrentLocation?: boolean; // 是否显示当前位置，默认true
    pageMarker?: string; // 页面标记，用于地图加载日志跟踪
}

const MapboxMapTemporary: React.FC<MapboxMapProps> = ({
                                                          deliveryAddresses,
                                                          onMarkerClick,
                                                          onMapClick,
                                                          mapRef,
                                                          getSavedMapState,
                                                          showCurrentLocation = true,
                                                          pageMarker
                                                      }) => {
    const { t } = useTranslation();
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<any>(null);

    // 地图加载打点
    useMapBoxPageLoadTrack(pageMarker);
    // 当前移动的位置
    const [currentLocation, setCurrentLocation] = useState<[number, number] | null>(null);
    // 初始化位置
    const [initialLocation, setInitialLocation] = useState<[number, number] | null>(null);
    const [markers, setMarkers] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    // 新增：用于控制标记创建的唯一标识符
    const markerGenerationId = useRef<number>(0);
    const isFirstLocation = useRef(true);

    // 监听当前位置
    useEffect(() => {
        if (!navigator.geolocation || !showCurrentLocation) return;

        const watchId = navigator.geolocation.watchPosition((position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation([longitude, latitude]);

            if (isFirstLocation.current) {
                setInitialLocation([longitude, latitude]);
                isFirstLocation.current = false;
                console.log('MapboxMap: 首次获取到位置');
            } else {
                console.error('MapboxMap: 监听位置发生了变更', position.coords);
            }
        }, (error) => {
            console.error('MapboxMap: 监听获取位置失败:', error);
        }, {
            enableHighAccuracy: true, // 高精度定位
            timeout: 5000,            // 超时5秒
            maximumAge: 0             // 不缓存
        })

        return () => {
            navigator.geolocation.clearWatch(watchId);
        }
    }, [showCurrentLocation]);

    // 初始化地图
    useEffect(() => {
        // 新增：刷新页面时强制清除可能残留的标记元素
        const clearAllExistingMarkers = () => {
            const allMarkers = document.querySelectorAll('.address-marker, .cluster-marker, .current-location-marker');
            allMarkers.forEach(marker => marker.remove());
        };
        clearAllExistingMarkers();

        if (!mapContainer.current || map.current) return;

        try {
            // 如果地址和当前位置都没获取到，不初始化地图，等待数据加载
            const hasAddressData = deliveryAddresses.length > 0;
            const hasInitialLocation = !!initialLocation;

            // 如果既没有地址数据也没有当前位置，等待数据加载完成后再初始化
            if (!hasAddressData && !hasInitialLocation) {
                return;
            }

            let initialCenter: [number, number];
            let initialZoom: number;

            // 优先使用保存的地图状态
            const savedState = getSavedMapState ? getSavedMapState() : null;
            if (savedState) {
                console.log('Using saved map state for initialization:', savedState);
                initialCenter = savedState.center;
                initialZoom = savedState.zoom;
            } else if (hasInitialLocation) {
                // 有当前位置，使用当前位置作为初始中心点
                initialCenter = initialLocation;
                initialZoom = 14;
            } else {
                // 没有当前位置但有地址数据，使用一个默认位置（稍后会通过地址处理调整）
                initialCenter = [0, 0]; // 临时位置，会被地址处理覆盖
                initialZoom = 12;
            }

            map.current = new (mapboxgl as any).Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: initialCenter,
                zoom: initialZoom
            });

            // 添加导航控件
            map.current.addControl(new (mapboxgl as any).NavigationControl(), 'top-right');

            // 设置外部地图引用
            if (mapRef) {
                mapRef.current = map.current;
            }

            map.current.on('load', () => {
                setIsLoading(false);
            });

            // 添加地图点击事件监听
            // 统一使用地图容器的点击事件，确保在所有平台上都能正常工作
            const handleMapContainerClick = (e: MouseEvent | TouchEvent) => {
                // 检查点击的目标是否是标记点
                const target = e.target as HTMLElement;
                if (target.closest('.mapboxgl-marker')) {
                    return; // 如果点击的是标记点，不触发地图点击
                }
                if (onMapClick) {
                    onMapClick();
                }
            };

            if (mapContainer.current) {
                mapContainer.current.addEventListener('click', handleMapContainerClick);
            }

            return () => {
                if (mapContainer.current) {
                    mapContainer.current.removeEventListener('click', handleMapContainerClick);
                }
                if (map.current) {
                    map.current.remove();
                    map.current = null;
                }
            };
        } catch (error) {
            console.error('MapboxMap: 地图初始化失败:', error);
        }
    }, [initialLocation, deliveryAddresses.length]);

    // 添加当前定位标记
    useEffect(() => {
        if (!map.current || !currentLocation || !showCurrentLocation) return;

        try {
            // 清除之前的定位标记
            const existingLocationMarker = document.querySelector('.current-location-marker');
            if (existingLocationMarker) {
                existingLocationMarker.remove();
            }

            // 添加当前定位标记 - 使用蓝色圆圈样式
            const locationMarker = new (mapboxgl as any).Marker({
                element: createLocationMarker()
            })
                .setLngLat(currentLocation)
                .addTo(map.current);

      // 添加定位标记的类名以便后续清除
      const markerElement = locationMarker.getElement();
      markerElement.classList.add('current-location-marker');
    } catch (error) {
      console.error('MapboxMap: 添加位置标记失败:', error);
    }
  }, [currentLocation, map.current, showCurrentLocation]);

    // 处理地址转换为坐标并添加标记
    useEffect(() => {
        // 新增：生成新的标记ID，用于标记本次生成的标记
        const currentGenerationId = ++markerGenerationId.current;
        if (!map.current) return;

        // 清除之前的标记 - 更彻底的清理方式
        const clearAllMarkers = () => {
            // 清除状态中的标记
            markers.forEach(marker => {
                if (marker && marker.remove) {
                    marker.remove();
                }
            });

            // 只清除地址标记，保留当前位置标记
            const addressMarkerElements = map.current.getContainer().querySelectorAll('.address-marker,.cluster-marker');
            addressMarkerElements.forEach((element: Element) => {
                if (element && element.parentNode) {
                    element.parentNode.removeChild(element);
                }
            });
        };

        clearAllMarkers();
        const newMarkers: any[] = [];

        // 批量处理地址
        const processAddresses = async () => {
            const bounds = new (mapboxgl as any).LngLatBounds();
            let processedCount = 0;
            // 第一步：获取所有地址的坐标并分组
            const addressCoordinates: any[] = [];
            for (let i = 0; i < deliveryAddresses.length; i++) {
                // 新增：检查是否是最新的标记生成请求，不是则终止
                if (markerGenerationId.current !== currentGenerationId) {
                    return;
                }
                const addressData = deliveryAddresses[i];

                try {
                    let lng: number | undefined, lat: number | undefined;

                    // 优先使用已有的经纬度数据
                    if (addressData.latitude && addressData.longitude) {
                        lng = addressData.longitude;
                        lat = addressData.latitude;
                        console.log(`Using existing coordinates for ${addressData.address}:`, { lat, lng });
                    } else {
                        console.log(`Geocoding address: ${addressData.address}`);
                        const response = await fetch(
                            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressData.address)}.json?access_token=${(mapboxgl as any).accessToken}&limit=1`
                        );

                        const data = await response.json();

                        if (data.features && data.features.length > 0) {
                            [lng, lat] = data.features[0].center;
                            console.log(`Geocoded ${addressData.address} to:`, { lat, lng });
                        } else {
                            console.warn(`Failed to geocode address: ${addressData.address}`);
                            continue; // 跳过这个地址
                        }
                    }

                    if (lng && lat) {
                        addressCoordinates.push({
                            ...addressData,
                            lng,
                            lat
                        });
                    }
                } catch (error) {
                    console.error(`MapboxMap: 地址解析错误: ${addressData.address}`, error);
                }
            }

            try{
                // 首次检查是否是最新的标记生成请求
                if (markerGenerationId.current !== currentGenerationId) {
                    return;
                }

          // 第二步：根据经纬度分组，相同经纬度的订单聚合为一组
          const addressGroups = new Map();
          addressCoordinates.forEach(address => {
              // 使用经纬度作为分组 key（保留6位小数，约11cm精度）
              const lat = address.lat.toFixed(6);
              const lng = address.lng.toFixed(6);
              const key = `${lat},${lng}`;
              if (!addressGroups.has(key)) {
                  addressGroups.set(key, []);
              }
              addressGroups.get(key).push(address);
          });

          // 第三步：为每个分组创建标记
          addressGroups.forEach((addresses, key) => {
              const firstAddress = addresses[0];
              const lng = firstAddress.lng;
              const lat = firstAddress.lat;
              let marker;
              if (addresses.length > 1) {
                  // 多个地址在同一位置，创建集群标记
                  // 检查集群中是否有异常订单，如果有则使用红色
                  const hasAbnormalOrder = addresses.some((addr: any) => addr.isAbnormal);
                  const clusterColor = hasAbnormalOrder ? '#EF4444' : firstAddress.localMarkerColor!;

                  marker = new (mapboxgl as any).Marker({
                      element: createClusterMarker(firstAddress.stopNumber,addresses.length,clusterColor)
                  }).setLngLat([lng, lat]).addTo(map.current!);
                  // 添加集群标记点击事件
                  const handleMarkerClick = (e: Event) => {
                      e.stopPropagation();
                      if (onMarkerClick) {
                          onMarkerClick({
                              isCluster: true,
                              clusterAddresses: addresses,
                              longitude: lng,
                              latitude: lat
                          });
                      }
                  };
                  marker.getElement().addEventListener('click', handleMarkerClick);
              } else {
                  // 单个地址，创建普通标记
                  let formattedStopNumber = formatStopNumber(parseInt(firstAddress.stopNumber.toString()), firstAddress.subStopNumber);
                  if( isNaN(parseInt(firstAddress.stopNumber.toString()))){
                      //兼容揽收地图起点和终点显示
                      formattedStopNumber=firstAddress.stopNumber.toString()
                  }
                  marker = new (mapboxgl as any).Marker({
                      element: createNumberedMarker(formattedStopNumber, firstAddress.localMarkerColor!)
                  }).setLngLat([lng, lat]).addTo(map.current!);
                  // 添加单个标记点击事件 - 保持原有数据结构不变
                  const handleSingleMarkerClick = (e: Event) => {
                      e.stopPropagation();
                      if (onMarkerClick) {
                          onMarkerClick({
                              id: firstAddress.id,
                              address: firstAddress.address,
                              trackingNumber: firstAddress.trackingNumber,
                              deliveryTaskOrderId: firstAddress.deliveryTaskOrderId,
                              coordinates: [lng, lat],
                              markerNumber: firstAddress.stopNumber,
                              isAbnormal: firstAddress.isAbnormal
                          });
                      }
                  };
                  marker.getElement().addEventListener('click', handleSingleMarkerClick);
              }

                    newMarkers.push(marker);
                    bounds.extend([lng, lat]);
                    processedCount++;
                });
                // 新增：检查是否是最新的标记生成请求
                if (markerGenerationId.current !== currentGenerationId) {
                    return;
                }
            }catch (error) {
                console.error(`MapboxMap: create 错误:`, error);
            }

            setMarkers(newMarkers);

      // 如果有保存的状态，跳过自动调整视图
      const savedState = getSavedMapState ? getSavedMapState() : null;
      if (!savedState) {
        // 如果有地址数据，直接使用框起来的逻辑
        if (processedCount > 0) {
          if (currentLocation && showCurrentLocation) {
            // 有当前位置和大头针，都框在一起
            bounds.extend(currentLocation);
          }
          map.current!.fitBounds(bounds, {
            padding: 50,
            maxZoom: 18,
            duration: 1000 // 动画时长1秒
          });
        } else {
          // 没有大头针数据的情况
          if (currentLocation && showCurrentLocation) {
            // 有当前位置，没有大头针，展示中心位置为当前位置
            map.current!.flyTo({
              center: currentLocation,
              zoom: 16,
              duration: 1000 // 动画时长1秒
            });
          }
          // 如果既没有当前位置也没有大头针数据，不进行任何视野调整
        }
      }
    };
    processAddresses();
  }, [deliveryAddresses, map.current, initialLocation]);

  // 组件卸载时清理标记
  useEffect(() => {
    return () => {
      if (markers.length > 0) {
        markers.forEach(marker => {
          if (marker && marker.remove) {
            marker.remove();
          }
        });
      }
    };
  }, [markers]);

  return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div
            ref={mapContainer}
            style={{ width: '100%', height: '100%', minHeight: '400px' }}
        />
        {isLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              zIndex: 1000
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>{t('map.loading')}</div>
            </div>
        )}
      </div>
  );
};

export default MapboxMapTemporary; 